import React, { useEffect, useState } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../../components/ui/card';
import { Skeleton } from '../../../components/ui/skeleton';
import { Progress } from '../../../components/ui/progress';
import { IconChartPie, IconAlertTriangle } from '@tabler/icons-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import coverageVerificationAnalyticService from '../../../services/coverage-verification-analytics.service';

const CoverageOutcomesChart = ({ organization, dateRange, refreshKey }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const result = await coverageVerificationAnalyticService.getCoverageOutcomesDistribution({
          organization,
          dateRange,
        });
        setData(result);
      } catch (err) {
        console.error('Failed to fetch coverage outcomes:', err);
        setError('Failed to load coverage outcomes data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [organization, dateRange, refreshKey]);

  if (loading) {
    return (
      <Card className="shadow-sm border-0">
        <CardHeader>
          <CardTitle className="text-base font-bold">Coverage Outcomes Distribution</CardTitle>
          <CardDescription className="text-sm">
            Breakdown of coverage statuses and requirements
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Skeleton className="w-full h-64" />
            <Skeleton className="w-full h-32" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="shadow-sm border-0">
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 text-red-600">
            <IconAlertTriangle size={20} />
            {error}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data || data.total_checks === 0) {
    return (
      <Card className="shadow-sm border-0">
        <CardHeader>
          <CardTitle className="text-base font-bold">Coverage Outcomes Distribution</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center text-muted-foreground py-8">
            No coverage outcomes data available
          </div>
        </CardContent>
      </Card>
    );
  }

  // Build pharmacy coverage data - show covered with/without restrictions and not covered
  const pharmacyCoverageData = [];
  if (data.covered_no_restrictions > 0) pharmacyCoverageData.push({ name: 'Covered (No Restrictions)', value: data.covered_no_restrictions, color: '#22c55e' });
  if (data.covered_with_restrictions > 0) pharmacyCoverageData.push({ name: 'Covered (With Restrictions)', value: data.covered_with_restrictions, color: '#f59e0b' });
  if (data.pharmacy_not_covered > 0) pharmacyCoverageData.push({ name: 'Not Covered', value: data.pharmacy_not_covered, color: '#ef4444' });
  if (data.pharmacy_no_data > 0) pharmacyCoverageData.push({ name: 'No Data', value: data.pharmacy_no_data, color: '#94a3b8' });
  if (data.pharmacy_unknown > 0) pharmacyCoverageData.push({ name: 'Unknown', value: data.pharmacy_unknown, color: '#64748b' });

  return (
    <Card className="shadow-sm border-0">
      <CardHeader>
        <div className="flex items-center gap-2">
          <IconChartPie size={20} />
          <div className="flex-1">
            <CardTitle className="text-base font-bold">Coverage Outcomes Distribution</CardTitle>
            <CardDescription className="text-sm">
              Breakdown of coverage statuses and requirements
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Pharmacy Coverage Pie Chart */}
          <div>
            <h6 className="text-sm font-semibold mb-4">Pharmacy Coverage Status</h6>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={pharmacyCoverageData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {pharmacyCoverageData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div className="mt-2 space-y-1">
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Covered (No Restrictions):</span>
                <span className="font-semibold text-green-600">{data.covered_no_restrictions} ({data.covered_no_restrictions_rate?.toFixed(1)}%)</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Covered (With Restrictions):</span>
                <span className="font-semibold text-orange-600">{data.covered_with_restrictions} ({data.covered_with_restrictions_rate?.toFixed(1)}%)</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Not Covered:</span>
                <span className="font-semibold text-red-600">{data.pharmacy_not_covered} ({data.pharmacy_not_covered_rate?.toFixed(1)}%)</span>
              </div>
              <div className="flex justify-between text-xs border-t pt-1 mt-1">
                <span className="text-muted-foreground font-medium">Total Covered:</span>
                <span className="font-semibold text-green-700">{(data.covered_no_restrictions || 0) + (data.covered_with_restrictions || 0)} ({data.pharmacy_coverage_rate?.toFixed(1)}%)</span>
              </div>
            </div>
          </div>

          {/* Requirements Breakdown */}
          <div>
            <h6 className="text-sm font-semibold mb-4">Requirements & Restrictions</h6>
            
            <div className="space-y-4">
              <div>
                <div className="flex justify-between mb-2">
                  <span className="text-sm font-medium">Prior Authorization</span>
                  <span className="text-sm font-semibold text-orange-600">{data.pa_rate?.toFixed(1)}%</span>
                </div>
                <Progress value={data.pa_rate} className="h-2" indicatorClassName="bg-orange-500" />
                <div className="flex justify-between text-xs text-muted-foreground mt-1">
                  <span>✓ Required: {data.requires_pa}</span>
                  <span>✗ Not Required: {data.no_pa}</span>
                </div>
              </div>

              <div>
                <div className="flex justify-between mb-2">
                  <span className="text-sm font-medium">Step Therapy</span>
                  <span className="text-sm font-semibold text-cyan-600">{data.st_rate?.toFixed(1)}%</span>
                </div>
                <Progress value={data.st_rate} className="h-2" indicatorClassName="bg-cyan-500" />
                <div className="flex justify-between text-xs text-muted-foreground mt-1">
                  <span>✓ Required: {data.requires_st}</span>
                  <span>✗ Not Required: {data.no_st}</span>
                </div>
              </div>

              <div>
                <div className="flex justify-between mb-2">
                  <span className="text-sm font-medium">Quantity Limits</span>
                  <span className="text-sm font-semibold text-yellow-600">{data.ql_rate?.toFixed(1)}%</span>
                </div>
                <Progress value={data.ql_rate} className="h-2" indicatorClassName="bg-yellow-500" />
                <div className="flex justify-between text-xs text-muted-foreground mt-1">
                  <span>✓ Has Limits: {data.requires_ql}</span>
                  <span>✗ No Limits: {data.no_ql}</span>
                </div>
              </div>

              <div className="pt-3 border-t">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-sm font-medium">No Restrictions</span>
                  <span className="text-sm font-semibold text-green-600">{data.no_restrictions_rate?.toFixed(1)}%</span>
                </div>
                <p className="text-xs text-muted-foreground">{data.no_restrictions} checks with no PA/ST/QL</p>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">Has Any Restriction</span>
                <span className="text-sm font-semibold text-orange-600">{data.has_restrictions} ({data.has_restrictions_rate?.toFixed(1)}%)</span>
              </div>
            </div>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t">
          <div className="text-center">
            <div className="text-xs text-muted-foreground mb-1">Total Checks</div>
            <div className="text-xl font-bold">{data.total_checks?.toLocaleString() || 0}</div>
          </div>
          <div className="text-center">
            <div className="text-xs text-muted-foreground mb-1">Pharmacy Covered</div>
            <div className="text-xl font-bold text-green-600">{((data.covered_no_restrictions || 0) + (data.covered_with_restrictions || 0)).toLocaleString()}</div>
          </div>
          <div className="text-center">
            <div className="text-xs text-muted-foreground mb-1">Medical Coverage</div>
            <div className="text-xl font-bold text-blue-600">{data.has_medical_coverage?.toLocaleString() || 0}</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default CoverageOutcomesChart;
